import React, { FC, Fragment, JSX } from 'react'
import classNames from 'classnames'

import { CreateAccountFooter } from 'containers/createAccount/components/createAccountFooter/CreateAccountFooter'
import { Checkbox, Input, Text } from 'components/ui-kit'
import { AppHeader } from 'components/appHeader/AppHeader'
import { PageWrapper } from 'components/pageWrapper/PageWrapper'
import { FormFieldsData, FormMeta, HandleFieldChange, SetFormElementRef } from 'types/form'

import styles from './CreateAccountView.module.scss'

interface Props {
  formData: FormFieldsData
  formMeta: FormMeta
  handleFieldChange: HandleFieldChange
  setFormElementRef: SetFormElementRef
  handleCreateAccount: () => void
  isBlurred: boolean
  isCreationPending: boolean
  Modals: JSX.Element
  handlePrivacyPolicyModalOpen: (event: React.MouseEvent) => void
}

export const CreateAccountView: FC<Props> = ({
  formData,
  formMeta,
  handleFieldChange,
  setFormElementRef,
  handleCreateAccount,
  isBlurred,
  isCreationPending,
  Modals,
  handlePrivacyPolicyModalOpen,
}) => (
  <div className={classNames(styles.createAccount, { [styles.blurred]: isBlurred })}>
    <AppHeader />
    <PageWrapper>
      <div className={styles.stepWizard}>
        <div className={styles.wizardContainer}>
          <div>
            <Text type="heading-2">Create Account to Start a New Application</Text>
          </div>
          <div>
            <Text type="body-1" className={styles.infoText}>
              All fields marked with the red asterisks (<span className={styles.requiredAsterisk}>*</span>) are
              mandatory to fill in.
            </Text>
          </div>
          <div className={styles.formSection}>
            <div className={styles.formSectionName}>
              <Text type="sub-head-1">Name</Text>
              <br />
              <Text type="body-2">You will not be able to change your name until after you are hired.</Text>
            </div>
            <div className={styles.formRow}>
              <Input {...formData['firstName']} label="Legal First Name" onChange={handleFieldChange('firstName')} />
              <Input {...formData['middleName']} label="Legal Middle Name" onChange={handleFieldChange('middleName')} />
              <Input {...formData['lastName']} label="Legal Last Name" onChange={handleFieldChange('lastName')} />
            </div>
          </div>
          <div className={styles.formSection}>
            <div className={styles.formSectionName}>
              <Text type="sub-head-1">Email</Text>
              <br />
              <Text type="body-2">
                Your email will be used for login. You will not be able to change your email until after you are hired.
              </Text>
            </div>
            <div className={styles.formRow}>
              <Input
                {...formData['email']}
                label="Email"
                onChange={handleFieldChange('email')}
                setInputRef={setFormElementRef('email')}
              />
              <Input
                {...formData['verifyEmail']}
                label="Email Confirmation"
                onChange={handleFieldChange('verifyEmail')}
              />
            </div>
          </div>
          <div className={styles.formSection}>
            <div className={styles.formSectionName}>
              <Text type="sub-head-1">Password</Text>
              <br />
              <Text type="body-2">
                IMPORTANT: You will need to remember this password to verify your account and continue your application.
              </Text>
            </div>
            <div className={styles.formRow}>
              <Input
                {...formData['password']}
                label="Password"
                type="password"
                isCanSeePassword
                onChange={handleFieldChange('password')}
                hint="Min 8 characters, lowercase and uppercase letters."
              />
              <Input
                {...formData['verifyPassword']}
                label="Confirm Password"
                onChange={handleFieldChange('verifyPassword')}
                type="password"
              />
            </div>
          </div>
          <div className={classNames(styles.formSection, styles.withoutBorder)}>
            <div className={styles.formSectionName}>
              <Text type="sub-head-1">Privacy Policy</Text>
            </div>
            <div className={classNames(styles.formRow, styles.formRowCheckbox)}>
              <Checkbox
                isChecked={formData['privacyPolicy'].value}
                onChange={handleFieldChange('privacyPolicy')}
                textLabel={
                  <Fragment>
                    &nbsp;I accept the&nbsp;
                    <span onClick={handlePrivacyPolicyModalOpen} className={styles.modalTrigger}>
                      Privacy Policy.
                    </span>
                  </Fragment>
                }
              />
            </div>
          </div>
        </div>
      </div>
    </PageWrapper>
    {Modals}
    <CreateAccountFooter
      isValid={formMeta.isValid}
      isPending={isCreationPending}
      handleCreateAccount={handleCreateAccount}
    />
  </div>
)
